import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useAuth, authService } from "auth";
import { AuthFormField } from "components/auth-form/AuthForm";
import {
  setMerchantId,
  setMerchantMetadata,
  setUserType,
} from "store/userSlice";
import { apiErrorMessage } from "utils/errors/parseApiError";
import {
  MERCHANT,
  MERCHANT_NEEDS_PASSWORD_CHANGE,
} from "components/protected-route/access";

export interface UseLoginFormReturn {
  fields: AuthFormField[];
  submitRef: React.RefObject<HTMLButtonElement>;
  isSubmitDisabled: boolean;
  showLoading: boolean;
  loadingMsg: string;
  showError: boolean;
  errorMsg: string;
  handleLogin: () => Promise<void>;
  handleCloseError: () => void;
}

export function useLoginForm(): UseLoginFormReturn {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    signIn,
    loading: authLoading,
    error: authError,
    clearError,
  } = useAuth();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showLoading, setShowLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const passwordRef = useRef<HTMLInputElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  const openError = (msg: string) => {
    setLoadingMsg("");
    setShowLoading(false);
    setErrorMsg(msg);
    setShowError(true);
  };

  const handleCloseError = () => {
    clearError();
    setShowLoading(false);
    setShowError(false);
    setErrorMsg("");
  };

  const resetForm = () => {
    setUsername("");
    setPassword("");
  };

  const handleLogin = async () => {
    try {
      setLoadingMsg("Signing in…");
      setShowLoading(true);
      await signIn(username, password);

      const currentMerchant = authService.getCurrentMerchant();
      if (!currentMerchant) {
        setShowLoading(false);
        return;
      }
      dispatch(setUserType(MERCHANT_NEEDS_PASSWORD_CHANGE));
      setLoadingMsg("Loading account…");
      const profile = await authService.getMerchantProfile(currentMerchant.uid);
      dispatch(setMerchantMetadata(profile));
      // Never the typed identifier: this field accepts an email too, and storing an address as
      // the merchantId sends every merchant-scoped request out with a bad id.
      const merchantId = profile.merchantId;
      if (profile.mustChangePassword) {
        setShowLoading(false);
        navigate("/change-password", {
          state: { merchantId },
        });
        return;
      }

      setShowLoading(false);

      dispatch(setUserType(MERCHANT));
      dispatch(setMerchantId(merchantId ?? ""));
      navigate("/merchant", {
        replace: true,
      });
    } catch (err: any) {
      setShowLoading(false);
      resetForm();
      // setUserType ran before the profile load, so a throw after it would leave a valid
      // userType behind — GuestOnlyRoute would then swallow this error and open a dashboard
      // with no merchantId. Roll it back so the failure stays on the login page.
      dispatch(setUserType(undefined));
      openError(
        apiErrorMessage(err, "Invalid credentials. Please check your details."),
      );
    }
  };

  const focusPassword = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") passwordRef.current?.focus();
  };

  const submitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") submitRef.current?.click();
  };

  const fields: AuthFormField[] = [
    {
      name: "username",
      label: "Email / Merchant ID",
      value: username,
      onChange: setUsername,
      onKeyDown: focusPassword,
      placeholder: "Enter your email or merchant ID",
      autoComplete: "username",
    },
    {
      name: "password",
      label: "Password",
      type: "password",
      value: password,
      onChange: setPassword,
      onKeyDown: submitOnEnter,
      placeholder: "Enter your password",
      autoComplete: "current-password",
      inputRef: passwordRef,
    },
  ];

  const isShowingLoading = showLoading || authLoading;
  const isShowingError = showError || Boolean(authError);
  const resolvedErrorMsg = showError ? errorMsg : (authError?.message ?? "");

  return {
    fields,
    submitRef,
    isSubmitDisabled: !username || !password,
    showLoading: isShowingLoading,
    loadingMsg,
    showError: isShowingError,
    errorMsg: resolvedErrorMsg,
    handleLogin,
    handleCloseError,
  };
}
